<template>
  <Teleport to="body">
    <div class="toast-area">
      <ToastComponent :visible="toast.visible" :type="toast.type" :message="toast.message" />
    </div>
  </Teleport>

  <Teleport to="body">
  <div class="modal-overlay">
    <div class="modal-container" @click="closeDropdowns" @click.stop ref="modalRef">
      <div class="modal-header">
        <h1 class="modal-title">Edit Technician</h1>
        <button class="close-btn" @click="handleClose">
          <img :src="closeFillIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <div class="form-container">
          <!-- Name -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Name </label>
            <div class="input-wrapper" :class="{ error: errors.name }">
              <input
                v-model="formData.name"
                type="text"
                class="form-input"
                placeholder="Enter name"
                :maxlength="techFieldLimits.name"
                @blur="handleNameBlur"
                @input="handleNameInput"
              />
              <img v-if="errors.name" :src="errorIcon" alt="error" class="error-icon" />
            </div>
          </div>

          <!-- Mobile number -->
          <div class="form-group">
            <label class="form-label">
              <span class="required">*</span> Mobile number (used to log in)
            </label>
            <div class="mobile-input-wrapper" :class="{ error: errors.phone }">
              <span class="country-code">+1</span>
              <input
                :value="formData.phone"
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                class="form-input mobile-input"
                placeholder="Enter mobile number"
                maxlength="10"
                @input="handleMobileInput"
                @blur="handleMobileBlur"
              />
              <img v-if="errors.phone" :src="errorIcon" alt="error" class="error-icon" />
            </div>
            <span v-if="errors.phone" class="error-text"
              >Please enter a 10-digit mobile number</span
            >
          </div>

          <!-- Upload avatar -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Upload avatar </label>
            <div class="upload-container" :class="{ error: errors.avatar }">
              <div v-if="!previewUrl" class="upload-area" @click="triggerUpload">
                <img :src="photoIcon" alt="photo" class="upload-icon" />
                <span class="upload-text">Upload</span>
              </div>
              <div v-else class="uploaded-image-wrapper">
                <img :src="previewUrl" alt="uploaded" class="uploaded-image" />
                <div class="delete-overlay" @click="removeImage">
                  <img :src="deleteIcon" alt="delete" class="delete-icon" />
                  <span class="delete-text">Delete</span>
                </div>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="file-input"
                @change="handleFileUpload"
              />
            </div>
          </div>

          <!-- Role + License No -->
          <div class="form-row">
            <div class="form-group half">
              <label class="form-label"> <span class="required">*</span> Role </label>
              <div
                class="custom-select-wrapper"
                @click.stop
                :class="{ error: errors.work_role }"
                @focusout="handleRoleFocusOut"
              >
                <div
                  class="custom-select"
                  tabindex="0"
                  :class="{ 'is-open': activeDropdown === 'role' }"
                  @click="toggleDropdown('role')"
                >
                  <span class="custom-select-value" :class="{ 'placeholder-text': !formData.work_role }">
                    {{
                      formData.work_role === 'inspector'
                        ? 'Inspector'
                        : formData.work_role === 'technician'
                          ? 'Technician'
                          : 'Select role'
                    }}
                  </span>
                  <div class="select-trail">
                    <img
                      v-if="errors.work_role"
                      :src="errorIcon"
                      alt="error"
                      class="error-icon-select"
                    />
                    <img :src="downArrowIcon" alt="dropdown" class="select-arrow" />
                  </div>
                </div>
                <div v-if="activeDropdown === 'role'" class="custom-dropdown">
                  <div class="dropdown-item" :class="{ selected: formData.work_role === 'inspector' }" @click="selectRole('inspector')">Inspector</div>
                  <div class="dropdown-item" :class="{ selected: formData.work_role === 'technician' }" @click="selectRole('technician')">Technician</div>
                </div>
              </div>
            </div>
            <div class="form-group half">
              <label class="form-label"><span style="visibility: hidden">*</span></label>
              <div class="input-wrapper" :class="{ error: errors.license_no }">
                <input
                  v-model="formData.license_no"
                  type="text"
                  class="form-input"
                  placeholder="Enter licence No."
                  @blur="handleLicenseBlur"
                  @input="handleLicenseInput"
                />
                <img v-if="errors.license_no" :src="errorIcon" alt="error" class="error-icon" />
              </div>
            </div>
          </div>

          <!-- SIN# -->
          <div class="form-row">
            <div class="form-group half">
              <label class="form-label"> <span class="required">*</span> SIN#/HST#</label>
              <div
                class="custom-select-wrapper"
                @click.stop
                :class="{ error: errors.sin_type }"
                @focusout="handleSinTypeFocusOut"
              >
                <div
                  class="custom-select"
                  tabindex="0"
                  :class="{ 'is-open': activeDropdown === 'sinType' }"
                  @click="toggleDropdown('sinType')"
                >
                  <span class="custom-select-value" :class="{ 'placeholder-text': !formData.sin_type }">
                    {{
                      formData.sin_type === 'individual'
                        ? 'Individual'
                        : formData.sin_type === 'business'
                          ? 'Business'
                          : 'Select SIN type'
                    }}
                  </span>
                  <div class="select-trail">
                    <img v-if="errors.sin_type" :src="errorIcon" alt="error" class="error-icon-select" />
                    <img :src="downArrowIcon" alt="dropdown" class="select-arrow" />
                  </div>
                </div>
                <div v-if="activeDropdown === 'sinType'" class="custom-dropdown">
                  <div class="dropdown-item" :class="{ selected: formData.sin_type === 'individual' }" @click="selectSinType('individual')">Individual</div>
                  <div class="dropdown-item" :class="{ selected: formData.sin_type === 'business' }" @click="selectSinType('business')">Business</div>
                </div>
              </div>
            </div>
            <div class="form-group half">
              <label class="form-label">
                <span style="visibility: hidden; user-select: none">*</span>
              </label>
              <div class="input-wrapper" :class="{ error: errors.sin_number }">
                <input
                  v-model="formData.sin_number"
                  type="text"
                  class="form-input"
                  placeholder="Enter SIN#"
                  :maxlength="techFieldLimits.sinNumber"
                  @blur="handleSinBlur"
                  @input="handleSinInput"
                />
                <img v-if="errors.sin_number" :src="errorIcon" alt="error" class="error-icon" />
              </div>
            </div>
          </div>

          <!-- Residency status -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Residency status </label>
            <div class="custom-select-wrapper" @click.stop :class="{ error: errors.residency_status }" @focusout="handleResidencyFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': activeDropdown === 'residency' }"
                @click="toggleDropdown('residency')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.residency_status }">
                  {{
                    formData.residency_status === 'canadian_citizenship'
                      ? 'Canadian Citizenship'
                      : formData.residency_status === 'permanent_resident'
                        ? 'Permanent Resident'
                        : formData.residency_status === 'work_permit'
                          ? 'Work Permit'
                          : 'Select residency status'
                  }}
                </span>
                <div class="select-trail">
                  <img
                    v-if="errors.residency_status"
                    :src="errorIcon"
                    alt="error"
                    class="error-icon-select"
                  />
                  <img :src="downArrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="activeDropdown === 'residency'" class="custom-dropdown">
                <div class="dropdown-item" :class="{ selected: formData.residency_status === 'canadian_citizenship' }" @click="selectResidency('canadian_citizenship')">
                  Canadian Citizenship
                </div>
                <div class="dropdown-item" :class="{ selected: formData.residency_status === 'permanent_resident' }" @click="selectResidency('permanent_resident')">
                  Permanent Resident
                </div>
                <div class="dropdown-item" :class="{ selected: formData.residency_status === 'work_permit' }" @click="selectResidency('work_permit')">Work Permit</div>
              </div>
            </div>
          </div>

          <!-- Address -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Address </label>
            <div class="input-wrapper" :class="{ error: errors.address }">
              <input
                v-model="formData.address"
                type="text"
                class="form-input"
                placeholder="Enter address"
                :maxlength="techFieldLimits.address"
                @blur="handleAddressBlur"
                @input="handleAddressInput"
              />
              <img v-if="errors.address" :src="errorIcon" alt="error" class="error-icon" />
            </div>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label class="form-label"> <span class="required">*</span> Email </label>
            <div class="input-wrapper" :class="{ error: errors.email }">
              <input
                v-model="formData.email"
                type="email"
                class="form-input"
                placeholder="Enter Email"
                :maxlength="techFieldLimits.email"
                @blur="handleEmailBlur"
                @input="handleEmailInput"
              />
              <img v-if="errors.email" :src="errorIcon" alt="error" class="error-icon" />
            </div>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="form-actions">
        <div class="button-group">
          <button type="button" class="btn btn-cancel" @click="handleCancel">Cancel</button>
          <button type="button" class="btn btn-submit" :disabled="submitting" @click="handleSubmit">
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, inject, watch, onMounted } from 'vue'
import { updateTechnician } from '@/api/techniciansupdate'
import { getTechnicianDetail } from '@/api/detailTechnicians'
import request from '@/api/request'
import ToastComponent from '@/components/ToastComponent.vue'
import photoIcon from '@/assets/photo.png'
import downArrowIcon from '@/assets/downarrow.png'
import errorIcon from '@/assets/alert.png'
import deleteIcon from '@/assets/delete_white@2x.png'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import '@/styles/custom-select-trail.css'
import { FIELD_LIMITS } from '@/constants/fieldLimits'
import { validateFieldLimits, sanitizeDigitsOnly } from '@/utils/fieldLimitValidation'

const techFieldLimits = FIELD_LIMITS.technician

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const props = defineProps<{
  technicianId: string
}>()

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => {},
)

const submitting = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const activeDropdown = ref<string | null>(null)
const previewUrl = ref<string>('')

function validateDropdownField(key: string): void {
  if (key === 'role') errors.work_role = isEmpty(formData.work_role)
  else if (key === 'sinType') errors.sin_type = isEmpty(formData.sin_type)
  else if (key === 'residency') errors.residency_status = isEmpty(formData.residency_status)
}

function closeDropdowns(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (target.closest('.custom-select-wrapper')) return
  if (activeDropdown.value) {
    validateDropdownField(activeDropdown.value)
  }
  activeDropdown.value = null
}

function toggleDropdown(key: string) {
  const wasOpen = activeDropdown.value === key
  activeDropdown.value = wasOpen ? null : key
  if (wasOpen) validateDropdownField(key)
}

function selectRole(value: string) {
  formData.work_role = value
  activeDropdown.value = null
  errors.work_role = false
}

function selectSinType(value: string) {
  formData.sin_type = value
  activeDropdown.value = null
  errors.sin_type = false
}

function selectResidency(value: string) {
  formData.residency_status = value
  activeDropdown.value = null
  errors.residency_status = false
}

function getAvatarUrl(avatar: string): string {
  if (!avatar) return ''
  if (avatar.startsWith('http')) return avatar
  return `${import.meta.env.VITE_BASE_URL || ''}${avatar}`
}

interface SettlementItem {
  type: string
  value: number | null
}

interface SettlementConfig {
  essential_care: SettlementItem
  enhanced_care: SettlementItem
  premium_care: SettlementItem
  follow_up_care: SettlementItem
  add_on_service: SettlementItem
}

interface TechnicianFormData {
  id: number
  name: string
  phone_code: string
  phone: string
  avatar: string | File | null
  work_role: string
  license_no: string
  sin_type: string
  sin_number: string
  residency_status: string
  address: string
  email: string
  settlement_config: SettlementConfig
}

const formData = reactive<TechnicianFormData>({
  id: 0,
  name: '',
  phone_code: '+1',
  phone: '',
  avatar: null,
  work_role: '',
  license_no: '',
  sin_type: '',
  sin_number: '',
  residency_status: '',
  address: '',
  email: '',
  settlement_config: {
    essential_care: { type: 'percentage_sba', value: null },
    enhanced_care: { type: 'fixed_before_tax', value: null },
    premium_care: { type: 'hourly', value: null },
    follow_up_care: { type: 'percentage_sba', value: 20 },
    add_on_service: { type: 'fixed_before_tax', value: 20 },
  },
})

const errors = reactive({
  name: false,
  phone: false,
  avatar: false,
  work_role: false,
  license_no: false,
  sin_type: false,
  sin_number: false,
  residency_status: false,
  address: false,
  email: false,
})

function isEmpty(value: string | undefined | null): boolean {
  return !String(value ?? '').trim()
}

function isFocusLeavingWrapper(event: FocusEvent): boolean {
  const wrapper = event.currentTarget as HTMLElement
  return !wrapper.contains(event.relatedTarget as Node)
}

function handleNameBlur() {
  errors.name = isEmpty(formData.name)
}

function handleNameInput() {
  if (!isEmpty(formData.name)) errors.name = false
}

function handleMobileBlur() {
  errors.phone = isEmpty(formData.phone) || !/^\d{10}$/.test(formData.phone)
}

function handleLicenseBlur() {
  errors.license_no = isEmpty(formData.license_no)
}

function handleLicenseInput() {
  if (!isEmpty(formData.license_no)) errors.license_no = false
}

function handleSinBlur() {
  errors.sin_number = isEmpty(formData.sin_number)
}

function handleSinInput() {
  if (!isEmpty(formData.sin_number)) errors.sin_number = false
}

function handleAddressBlur() {
  errors.address = isEmpty(formData.address)
}

function handleAddressInput() {
  if (!isEmpty(formData.address)) errors.address = false
}

function handleEmailBlur() {
  errors.email = isEmpty(formData.email)
}

function handleEmailInput() {
  if (!isEmpty(formData.email)) errors.email = false
}

function handleRoleFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.work_role = isEmpty(formData.work_role)
}

function handleSinTypeFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.sin_type = isEmpty(formData.sin_type)
}

function handleResidencyFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.residency_status = isEmpty(formData.residency_status)
}

const toast = reactive({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})

function triggerUpload() {
  fileInput.value?.click()
}

function handleMobileInput(event: Event) {
  formData.phone = sanitizeDigitsOnly(
    (event.target as HTMLInputElement).value,
    techFieldLimits.phone,
  )
  errors.phone = false
}

function hasAvatar(): boolean {
  if (formData.avatar instanceof File) return true
  if (typeof formData.avatar === 'string' && formData.avatar.trim()) return true
  return Boolean(previewUrl.value?.trim())
}

function handleFileUpload(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    formData.avatar = file
    previewUrl.value = URL.createObjectURL(file)
    errors.avatar = false
  }
}

function removeImage() {
  formData.avatar = null
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = ''
  }
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function validateForm(): boolean {
  let isValid = true

  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = false
  })

  if (!formData.name) {
    errors.name = true
    isValid = false
  }
  if (!formData.phone || !/^\d{10}$/.test(formData.phone)) {
    errors.phone = true
    isValid = false
  }
  if (!hasAvatar()) {
    errors.avatar = true
    isValid = false
  }
  if (!formData.work_role) {
    errors.work_role = true
    isValid = false
  }
  if (isEmpty(formData.license_no)) {
    errors.license_no = true
    isValid = false
  }
  if (!formData.sin_type) {
    errors.sin_type = true
    isValid = false
  }
  if (!formData.sin_number) {
    errors.sin_number = true
    isValid = false
  }
  if (!formData.residency_status) {
    errors.residency_status = true
    isValid = false
  }
  if (!formData.address) {
    errors.address = true
    isValid = false
  }
  if (!formData.email) {
    errors.email = true
    isValid = false
  }

  if (!isValid) {
    showToast('warning', 'Please complete all required fields.')
    return false
  }

  const limitErr = validateFieldLimits([
    { value: formData.name, max: techFieldLimits.name, label: 'Name' },
    { value: formData.phone, max: techFieldLimits.phone, label: 'Mobile number' },
    { value: formData.sin_number, max: techFieldLimits.sinNumber, label: 'SIN number' },
    { value: formData.address, max: techFieldLimits.address, label: 'Address' },
    { value: formData.email, max: techFieldLimits.email, label: 'Email' },
  ])
  if (limitErr) {
    showToast('warning', limitErr)
    return false
  }

  return true
}

async function uploadAvatarFile(): Promise<string> {
  if (!formData.avatar || typeof formData.avatar !== 'object') {
    return (formData.avatar as string) || ''
  }

  const uploadFormData = new FormData()
  uploadFormData.append('category', 'image')
  uploadFormData.append('file', formData.avatar)

  const response = await request.post('/api/admin/upload', uploadFormData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  const responseData = response as {
    code?: number
    msg?: string
    data?: { url?: string; path?: string }
  }

  const avatarUrl = responseData.data?.url || responseData.data?.path || ''

  if (!avatarUrl) {
    throw new Error('Avatar upload failed')
  }

  return avatarUrl
}

async function fetchTechnicianDetail() {
  if (!props.technicianId) {
    showToast('error', 'Technician ID not found')
    emit('close')
    return
  }

  try {
    const response = await getTechnicianDetail(props.technicianId)
    if (response.code === 200 && response.data) {
      // ✅ 修复最后一个any错误
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = response.data as any

      formData.id = data.id
      formData.name = data.name || ''
      formData.phone = (data.phone || '').replace(/[^\d]/g, '') || ''
      formData.avatar = data.avatar || ''
      formData.work_role = data.work_role || 'inspector'
      formData.license_no = data.license_no || ''
      formData.sin_type = data.sin_type || 'individual'
      formData.sin_number = data.sin_number || ''
      formData.residency_status = data.residency_status || 'canadian_citizenship'
      formData.address = data.address || ''
      formData.email = data.email || ''

      if (data.settlement_config) {
        formData.settlement_config.essential_care = {
          type: data.settlement_config.essential_care?.type || 'percentage_sba',
          value: data.settlement_config.essential_care?.value ?? null,
        }
        formData.settlement_config.enhanced_care = {
          type: data.settlement_config.enhanced_care?.type || 'fixed_before_tax',
          value: data.settlement_config.enhanced_care?.value ?? null,
        }
        formData.settlement_config.premium_care = {
          type: data.settlement_config.premium_care?.type || 'hourly',
          value: data.settlement_config.premium_care?.value ?? null,
        }
        formData.settlement_config.follow_up_care = {
          type: data.settlement_config.follow_up_care?.type || 'percentage_sba',
          value: data.settlement_config.follow_up_care?.value ?? 20,
        }
        formData.settlement_config.add_on_service = {
          type: data.settlement_config.add_on_service?.type || 'fixed_before_tax',
          value: data.settlement_config.add_on_service?.value ?? 20,
        }
      }

      if (data.avatar) {
        previewUrl.value = getAvatarUrl(data.avatar)
      }
    } else {
      showToast('error', 'Failed to load technician data')
      emit('close')
    }
  } catch (error) {
    console.error('Fetch detail error:', error)
    showToast('error', 'Network error')
    emit('close')
  }
}

async function handleSubmit() {
  if (!validateForm()) {
    return
  }

  submitting.value = true

  try {
    let avatarUrl = ''

    if (formData.avatar instanceof File) {
      showToast('success', 'Uploading avatar...')
      avatarUrl = await uploadAvatarFile()
    } else if (typeof formData.avatar === 'string' && formData.avatar.trim()) {
      avatarUrl = formData.avatar
    } else if (previewUrl.value && !previewUrl.value.includes('blob:')) {
      avatarUrl = previewUrl.value
    }

    if (!avatarUrl) {
      errors.avatar = true
      submitting.value = false
      showToast('warning', 'Please complete all required fields.')
      return
    }

    const payload = {
      id: Number(props.technicianId),
      name: formData.name,
      phone_code: formData.phone_code,
      phone: formData.phone,
      avatar: avatarUrl,
      work_role: formData.work_role,
      license_no: formData.license_no,
      sin_type: formData.sin_type,
      sin_number: formData.sin_number,
      residency_status: formData.residency_status,
      address: formData.address,
      email: formData.email,
      settlement_config: {
        follow_up_care: {
          type: 'percentage_sba',
          value: 28,
        },
        add_on_service: {
          type: 'fixed_before_tax',
          value: 20,
        },
      },
    }

    const res = await updateTechnician(payload)

    if (res.code === 200 || res.msg === 'successfully.') {
      emit('saved')
      emit('close')
    } else {
      showToast('error', res.msg || 'Update failed')
    }
  } catch (err: unknown) {
    console.error('Update error:', err)
    const errorMessage = err instanceof Error ? err.message : 'Network error'
    showToast('error', errorMessage)
  } finally {
    submitting.value = false
  }
}

function handleClose() {
  emit('close')
}

function handleCancel() {
  emit('close')
}

watch(
  () => props.technicianId,
  (newId) => {
    if (newId) {
      fetchTechnicianDetail()
    }
  },
  { immediate: true },
)

onMounted(() => {
  if (props.technicianId) {
    fetchTechnicianDetail()
  }
})
</script>
<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
}

.modal-container {
  background: #fff;
  border-radius: 12px;
  width: 530px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0;
  flex-shrink: 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  white-space: nowrap;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0 24px;
}

.form-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.form-group {
  margin-bottom: 16px;
}

.form-group.half {
  margin-bottom: 16px;
}

.required {
  color: #e54848;
  margin-right: 4px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.form-input,
.custom-select {
  height: 32px;
  line-height: 20px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  width: 100%;
  color: #000000;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
}

.form-input {
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  outline: none;
  transition: border-color 0.2s;
  background: #fff;
  color: #000000;
}

.form-input:hover {
  border-color: #e0e0e0;
}

.form-input:focus {
  border-color: #666666;
}

.input-wrapper.error .form-input {
  border: none;
  height: 32px;
}

.mobile-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  overflow: hidden;
  height: 32px;
  transition: border-color 0.2s;
}

.mobile-input-wrapper:hover {
  border-color: #e0e0e0;
}

.mobile-input-wrapper:focus-within {
  border-color: #666666;
}

.mobile-input-wrapper.error {
  border-color: #ff4d4f;
}

.country-code {
  position: relative;
  padding: 0 12px;
  height: 100%;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  white-space: nowrap;
}

.country-code::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e8e8e8;
}

.mobile-input {
  border: none;
  border-radius: 0;
  padding-left: 8px;
}

.upload-container {
  position: relative;
}

.upload-container.error .upload-area,
.upload-container.error .uploaded-image-wrapper {
  border-color: #ff4d4f;
}

.upload-container.error .upload-area {
  background: #fffafa;
}

.upload-area {
  width: 96px;
  height: 96px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
  background: #fff;
}

.upload-icon {
  width: 32px;
  height: 32px;
}

.upload-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.uploaded-image-wrapper {
  position: relative;
  width: 96px;
  height: 96px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.delete-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.uploaded-image-wrapper:hover .delete-overlay {
  opacity: 1;
}

.delete-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.delete-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #ffffff;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.file-input {
  display: none;
}

.custom-select-wrapper {
  position: relative;
}

.custom-select-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
}

.custom-select {
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
  user-select: none;
  display: flex;
  align-items: center;
  font-weight: 400;
}

.custom-select:hover,
.custom-select:focus {
  border-color: #e0e0e0;
  outline: none;
}

.custom-select.is-open {
  border-color: #666666;
}

.custom-select-wrapper.error .custom-select {
  border: none;
  height: 32px;
}

.placeholder-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #cccccc;
  line-height: 20px;
}

.select-arrow {
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
  transition: transform 0.2s ease;
}

.custom-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
  border: none;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-item {
  padding: 8px 16px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.custom-dropdown .dropdown-item.selected {
  font-weight: 600;
}

.error-icon {
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
}

.error-text {
  display: block;
  margin-top: 6px;
  font-size: 0.85rem;
  color: #ff4d4f;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 16px;
  align-items: flex-start;
}

.form-row .form-group.half {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
}

.form-row .form-group.half:last-child {
  padding-top: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 24px 24px;
  background: #fff;
  flex-shrink: 0;
}

.button-group {
  display: flex;
  gap: 16px;
}

.btn {
  width: 80px;
  height: 32px;
  padding: 0;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-cancel {
  background: #fff;
  border: 1px solid #000000;
  color: #000000;
}

.btn-cancel:hover {
  background: #000000;
  color: #ffffff;
}

.btn-submit {
  background: #000000;
  color: #fff;
}

.btn-submit:hover:not(:disabled) {
  background: #484848;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

.toast-area {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-body {
    padding: 16px;
  }
}
</style>
